import { OrbitControls } from "@react-three/drei";
import { useSpring, easings } from "@react-spring/three";
import { useFrame, useThree } from "@react-three/fiber";
import { useRef, useEffect, useMemo } from "react";
import * as THREE from "three";
import { OrbitControls as OrbitType } from 'three-stdlib'
import { useSocket } from "../../contexts/SocketContext";
import { createSafeInvalidate } from "../../tools/helpers";
import { StandardControlsEventMap } from "three-stdlib/controls/StandardControlsEventMap";
import { PlayerFacingDirection } from "../../types/SharedTypes";

type Coords = [number, number, number];
type CoordSet = {pos: Coords, target: Coords};

const COORDS: {idle: CoordSet, game: CoordSet} = {
  idle: {
    pos: [0, 20, 10],
    target: [0, 5, -10]
  },
  game: {
    pos: [0, 5.7, 4],
    target: [0, 5, 0]
  }
}
const MIN_AZIMUTH_ANGLE = -Math.PI / 9;
const MAX_AZIMUTH_ANGLE = Math.PI / 9;

export const ModOrbit = () => {

  const controlsRef = useRef<OrbitType>(null);
  const loopCounterRef = useRef(0);
  const {invalidate} = useThree();
  const safeInvalidate = createSafeInvalidate(invalidate);
  const {game, isCuttingDeck, updateUserFacing} = useSocket();

  const forward = useMemo(() => new THREE.Vector3(), []);

  const prevFacingRef = useRef<PlayerFacingDirection>(0);
  const debounceTimerRef = useRef<NodeJS.Timer>(null);

  // Idle loop for pre-game subtle camera sway
  const [spring, apiIdle] = useSpring({
    loop: { reverse: true },
    from: { x: -0.5, y: 0.05 },
    to: { x: 0.5, y: -0.05 },
    config: { duration: 8000, easing: easings.easeInOutSine},
    onStart: () => safeInvalidate(),
    onRest: () => {
      loopCounterRef.current += 1;
      safeInvalidate();

      if (loopCounterRef.current >= 4) apiIdle.pause();
    },
  }, []);

  // Transition spring for when the game starts
  const [{pos, target}, apiTransition] = useSpring({
    from: {
      pos: COORDS.idle.pos,
      target: COORDS.idle.target,
    },
    config: { duration: 700, easing: easings.easeInOutSine},
    onStart: () => invalidate(),
    onRest: () => invalidate(),
  }, []);

  useEffect(() => {
    //if no game, start idle anim. else start transition anim
    if (!game) {
      console.log('anim to idle');
      if (spring.x.isPaused) {
        loopCounterRef.current = 0;
        apiIdle.resume();
      }
      else apiIdle.start();
      apiTransition.start({
        to: {
          pos: COORDS.idle.pos,
          target: COORDS.idle.target,
        },
        config: {duration: 700}
      });
      // disable user-camera interaction
      controlsRef.current && (controlsRef.current.enabled = false);
    }
    else {
      console.log('anim to game');
      apiIdle.pause();
      apiTransition.start({
        to: {
          pos: COORDS.game.pos,
          target: COORDS.game.target,
        },
        config: {duration: 700},
        //when transition finishes, allow user to interact with camera
        onRest: () => {
          controlsRef.current && (controlsRef.current.enabled = true);
        }
      });
    }
  }, [!!game]);

  useEffect(() => {
    if (!isCuttingDeck) return;
    //kinda works
    apiTransition.start({
      from: {
        target: controlsRef.current?.target.toArray(),
      },
      to: {
        target: COORDS.game.target,
      },
      config: {duration: 300}
    });
  }, [isCuttingDeck]);

  const handleOrbitChange = (e?: THREE.Event<keyof StandardControlsEventMap, OrbitType>) => {
    if (!e) return;

    const min = MIN_AZIMUTH_ANGLE * 2;
    const max = MAX_AZIMUTH_ANGLE * 2;
    const section = (max - min) / 3;
    const angle = e.target.getAzimuthalAngle();

    let facing: PlayerFacingDirection;

    if (angle < min + section) facing = -1;
    else if (angle < min + section * 2) facing = 0;
    else facing = 1;

    if (prevFacingRef.current === facing) return;

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      prevFacingRef.current = facing;
      updateUserFacing(facing);
    }, 700);

  }

  useFrame((state) => {
    if (!controlsRef.current) return;

    if ((spring.x.isAnimating || spring.y.isAnimating) && !spring.x.isPaused) invalidate();

    //transition between idle and game cameras
    if (pos.isAnimating || target.isAnimating) {
      const p = pos.get();
      const t = target.get();
      state.camera.position.set(p[0], p[1], p[2]);
      controlsRef.current.target.set(t[0], t[1], t[2]);
      controlsRef.current.update();
      invalidate();
      return;
    }

    if (!game) {
      //manually update camera x position with idle animation values
      const p = pos.get();
      const t = target.get();
      state.camera.position.set(p[0] + spring.x.get(), p[1], p[2]);
      controlsRef.current.target.set(t[0], t[1], t[2]);
      //since we move the camera, we need to manually update the controls
      controlsRef.current.update();
      return;
    }

    //logic to give orbit controls a first-person feel
    state.camera.getWorldDirection(forward);
    if (!isCuttingDeck) controlsRef.current.target.copy(state.camera.position).add(forward);
  });

  return (
    <OrbitControls
      ref={controlsRef}
      onChange={handleOrbitChange}
      enablePan={false}
      enableDamping={false}
      rotateSpeed={-0.2}
      minAzimuthAngle={MIN_AZIMUTH_ANGLE}
      maxAzimuthAngle={MAX_AZIMUTH_ANGLE}
      minPolarAngle={Math.PI / 4}
      maxPolarAngle={Math.PI - Math.PI / 2}
      enableZoom={false}
      enableRotate={!isCuttingDeck}
    />
  );
}
